import {
    MiniSignal
} from "p3";
import definitions from "../../../definitions";
import GameScene from "../../../scenes/GameScene";
import ItemIndicator from "../../../ui/ItemIndicator";
import Actor from "../Actor";

export default class Player extends Actor {
    constructor() {
        super();

        this.signals.attack = new MiniSignal();
        this.signals.joystickStart = new MiniSignal();
        this.signals.joystickStop = new MiniSignal();

        this.moveSpeed = 2500;
        this._baseAttackRange = 160;
        this.attackRange = this._baseAttackRange;
        this._baseAttackPower = 1;
        this.attackPower = this._baseAttackPower;
        this.linearDamping = 6;
        this.collisionDamping = 12;
        this.disableInput = false;

        this.moveToTarget = null;
        this.moveToTargetOffset = new PIXI.Point();
        this.moveToTargetMinRadius = 20;
        this.moveToTargetMaxRadius = 160;

        this.collisionDynamic = true;
        this.collisionRadius = 25;
        this.collisionOffsetY = -22;
        this.collisionType = definitions.COLLISION_TYPE.CIRCLE;

        this._isMoving = false;

        this._joystickStart = null;
        this._joystickActive = false;
        this._joystickDeadzone = 80;

        this._inputDir = new P3.Vector2();
        this._isInputActive = false;

        this._item = null;
        this._animation = null;

        this._damageEffectFilter = null;
        this._damageEffectTween = null;
    }

    init() {
        super.init();

        this.initCollider(this.collisionX, this.collisionY);

        this._damageEffectFilter = new P3.filters.AdditiveFilter(0, 2);
    }

    destroy() {
        this.removeDamageEffect();

        super.destroy();
    }

    onViewAdded() {
        super.onViewAdded();

        this._item = new ItemIndicator();
        this._item.position.set(0, -254);
        this.addChild(this._item);
    }

    update() {
        super.update();
        this._move();
    }

    /**
     * @param {Entity} entity
     * @param {P3.Vector2} v
     */
    onCollisionEntity(entity, v) {
        super.onCollisionEntity(entity, v);

        this.x += v.x;
        this.y += v.y;

        v.normalize();

        const d = 1 - Math.exp(-this.collisionDamping * window.dt);

        this.velocity.x *= 1 - Math.abs(v.x) * d;
        this.velocity.y *= 1 - Math.abs(v.y) * d;
    }

    onCollisionTile(tile, v) {
        super.onCollisionTile(tile, v);

        this.x += v.x;
        this.y += v.y;

        v.normalize();

        const d = 1 - Math.exp(-this.collisionDamping * window.dt);

        this.velocity.x *= 1 - Math.abs(v.x) * d;
        this.velocity.y *= 1 - Math.abs(v.y) * d;
    }

    /**
     * @private
     */
    _move() {
        this._isInputActive = false;

        if (!this.disableInput) {
            this.moveToTarget = null;

            const dir = new P3.Vector2();
            let move;

            if (!PIXI.isMobile.any) {
                move = this._moveWASD(dir);
            }

            const joystick = this._moveJoystick(dir);
            if (joystick) {
                move = joystick;
            }

            if (move) {
                dir.normalize();
                this.velocity.set(this.velocity.x + dir.x * this.moveSpeed * window.dt, this.velocity.y + dir.y * this.moveSpeed * window.dt);
                this.facingDirection = this.velocity.x;

                this._inputDir = dir.clone();
                this._isInputActive = true;
            } else {
                this._inputDir.set(0);
            }
        }

        if (this.moveToTarget) {
            const force = new P3.Vector2();
            const target = new PIXI.Point(this.moveToTarget.x + this.moveToTargetOffset.x, this.moveToTarget.y + this.moveToTargetOffset.y);
            if (this._moveToTarget(target, force)) {
                this.velocity.set(this.velocity.x + force.x * window.dt, this.velocity.y + force.y * window.dt);
                this.facingDirection = this.velocity.x;
            }
        }

        if (this.velocity.lengthSq > 150) {
            if (!this._isMoving) {
                this._isMoving = true;
                this.onMoveStart();
            }
        } else {
            if (this._isMoving) {
                this._isMoving = false;
                this.onMoveStop();
            }
        }
    }

    /**
     * @param {P3.Vector2} dir
     * @returns {boolean}
     * @private
     */
    _moveWASD(dir) {
        let move = false;

        if (!document.hasFocus()) {
            const keys = ['A', 'LEFT', 'D', 'RIGHT', 'W', 'UP', 'S', 'DOWN'];
            for (let i in keys) {
                P3.input._keyMap[P3.keys[keys[i]]] = false;
            }
            return false;
        }

        if (P3.input.isKeyDown(P3.keys.A) || P3.input.isKeyDown(P3.keys.LEFT)) {
            dir.x -= 1;
            move = true;
        }
        if (P3.input.isKeyDown(P3.keys.D) || P3.input.isKeyDown(P3.keys.RIGHT)) {
            dir.x += 1;
            move = true;
        }
        if (P3.input.isKeyDown(P3.keys.W) || P3.input.isKeyDown(P3.keys.UP)) {
            dir.y -= 1;
            move = true;
        }
        if (P3.input.isKeyDown(P3.keys.S) || P3.input.isKeyDown(P3.keys.DOWN)) {
            dir.y += 1;
            move = true;
        }

        return move;
    }

    /**
     * @param {P3.Vector2} dir
     * @returns {boolean}
     * @private
     */
    _moveJoystick(dir) {
        let move = false;

        const touch = GameScene.active.joystickTouch;

        if (touch) {
            if (!this._joystickStart) {
                this._joystickStart = GameScene.active.joystickTouch.pos;
            } else {
                const joyDir = new P3.Vector2(touch.pos.x - this._joystickStart.x, touch.pos.y - this._joystickStart.y);
                const joyDeadzoneSq = this._joystickDeadzone * this._joystickDeadzone;

                if (joyDir.lengthSq > joyDeadzoneSq) {
                    dir.set(joyDir.x, joyDir.y);
                    move = true;

                    if (!this._joystickActive) {
                        this._joystickActive = true;
                        this.signals.joystickStart.dispatch(this._joystickStart);
                    }
                }
            }
        } else {
            this._joystickActive && this.signals.joystickStop.dispatch();
            this.resetJoystick();
        }
     
        return move;
    }

    /**
     * @param {object} target
     * @param {P3.Vector2} force
     * @private
     */
    _moveToTarget(target, force) {
        let move = true;

        force.set(target.x - this.x, target.y - this.y);
        const distance = force.length;
        force.x /= distance;
        force.y /= distance;

        let targetSpeed = Math.min(1, (distance / this.moveToTargetMaxRadius)) * this.moveSpeed;
        if (distance < this.moveToTargetMinRadius) {
            move = false;
            targetSpeed = 0;
        }
        force.scaleBy(targetSpeed);
        return move;
    }

    onMoveStart() {}

    onMoveStop() {}

    resetJoystick() {
        this._joystickActive = false;
        this._joystickStart = null;
    }

    /**
     * @param {Item} item
     * @param {number} [delay=0]
     * @return {Promise}
     */
    collectItem(item, delay = 0) {
        return this._item.animateCollect(item, delay);
    }

    /**
     * @param {number} amount
     */
    takeDamage(amount) {
        this.damageEffect().then();
    }

    /**
     * @param {number} [speed=definitions.PLAYER_DAMAGE_EFFECT.FAST]
     * @return {Promise}
     */
    damageEffect(speed = definitions.PLAYER_DAMAGE_EFFECT.FAST) {
        return new Promise((resolve) => {
            this.removeDamageEffect();

            this._damageEffectFilter.blendStrength = 0.3;
            this._damageEffectTween = GameScene.active.addTween(gsap.to(this._damageEffectFilter, speed, {
                blendStrength: 0,
                ease: Power2.easeOut,
                onStart: () => {
                    this.filters = [this._damageEffectFilter]
                },
                onComplete: () => {
                    this.filters = [];
                    resolve();
                }
            }));
        });
    }

    removeDamageEffect() {
        if (this._damageEffectTween) {
            GameScene.active.removeTween(this._damageEffectTween, true);
            this._damageEffectTween = null;
        }

        this._damageEffectFilter.blendStrength = 0;
        this.filters = [];
    }

    /**
     * @returns {boolean}
     */
    get isMoving() {
        return this._isMoving;
    }

    /**
     * @returns {PIXI.Point}
     */
    get joystickStart() {
        return this._joystickStart;
    }

    /**
     * @returns {boolean}
     */
    get joystickActive() {
        return this._joystickActive;
    }

    /**
     * @returns {number}
     */
    get joystickDeadzone() {
        return this._joystickDeadzone;
    }

    /**
     * @return {P3.Vector2}
     */
    get inputDir() {
        return this._inputDir;
    }

    /**
     * @return {boolean}
     */
    get isInputActive() {
        return this._isInputActive;
    }
}